/**
 * Shared state for the one-and-only silent SSO probe.
 *
 * `SilentSsoCheck` owns the probe; the navbar needs to know whether one is running so it can
 * show a pending state instead of committing to "logged out". They sit in different branches
 * of the layout tree, and there is exactly one probe per document, so a module-level store
 * read through `useSyncExternalStore` is a better fit than threading a context provider
 * above both — and it keeps the "one probe" invariant visible in one place.
 *
 * Phases:
 *   idle    — nothing has decided yet (the state at first paint)
 *   probing — the hidden iframe is up, waiting on raauth
 *   settled — we have our answer, or we decided not to ask (auth page, just logged out, this
 *             page load already asked). Terminal: the navbar may now commit to the logged-out
 *             UI.
 */

import { pathEquals } from "@/lib/path-match"

export const SSO_SILENT_PATH = "/sso-silent"

/**
 * Trailing-slash-tolerant check for the silent worker route. Getting it wrong breaks the probe
 * in a way nothing surfaces — it misidentifies the worker as "some other page that landed in
 * the iframe" — which is why the rule lives in `pathEquals` rather than being restated here.
 */
export function isSsoSilentPath(pathname: string | null | undefined): boolean {
   return pathEquals(pathname, SSO_SILENT_PATH)
}

export type SsoProbePhase = "idle" | "probing" | "settled"

let phase: SsoProbePhase = "idle"
const listeners = new Set<() => void>()

export function subscribeSsoProbe(listener: () => void): () => void {
   listeners.add(listener)
   return () => listeners.delete(listener)
}

export function getSsoProbePhase(): SsoProbePhase {
   return phase
}

/** Server snapshot: nothing has been probed during SSR. */
export function getSsoProbeServerPhase(): SsoProbePhase {
   return "idle"
}

export function setSsoProbePhase(next: SsoProbePhase): void {
   if (phase === next) return
   phase = next
   for (const listener of listeners) listener()
}
